'use client';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { api } from '@/trpc/react';
import { toast } from '@/components/ui/use-toast';
import AnimatedProgressBar from '@components/AnimatedProgress/AnimatedProgressBar';
import { useSession } from '@hooks/index';
import Image from 'next/image';
import AiGeneratedBadge from '@components/Badge/ai-generated-badge';
import { cn } from '@lib/utils';
import { CloudflareTransform } from '@lib/transformCloudflareImageUrl';
import { transformCloudflareImageUrl } from '@acme/lib';
interface PhotoPackSamplePhoto {
  imageUrl: string;
  id?: string;
  className?: string;
  themeId?: string;
  showSelfie?: boolean;
  selfieImageUrl?: string;
  forceTallAspectRatio?: boolean;
  cloudflareVariant?: CloudflareTransform['variant'];
  showAdminActions?: boolean;
}

export function PhotoPackSamplePhoto({
  imageUrl,
  id,
  className,
  themeId,
  showSelfie,
  selfieImageUrl,
  forceTallAspectRatio = false,
  cloudflareVariant = 'public',
  showAdminActions = false
}: PhotoPackSamplePhoto) {
  const [isRerunning, setIsRerunning] = useState(false);
  const utils = api.useUtils();
  const { user } = useSession();

  const { mutate: rerunSampleImage } = api.admin.rerunSampleImage.useMutation({
    onSuccess: () => {
      utils.admin.getThemeById.invalidate({ id: themeId! });
      toast({
        title: 'Success',
        description: 'Sample image regenerated successfully'
      });
      setIsRerunning(false);
    },
    onError: error => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
      setIsRerunning(false);
    }
  });

  const handleRerun = () => {
    if (!id) return;
    setIsRerunning(true);
    rerunSampleImage({
      sampleImageId: id
    });
  };

  return (
    <div className={`relative group ${className}`}>
      {showSelfie && selfieImageUrl && (
        <div className="absolute top-2 left-2 sm:top-4 sm:left-4 z-10 w-1/4 h-auto max-w-16 max-h-16 rounded-md md:rounded-xl overflow-hidden aspect-square shadow-[0_2.8px_2.2px_rgba(0,_0,_0,_0.034),_0_6.7px_5.3px_rgba(0,_0,_0,_0.048),_0_12.5px_10px_rgba(0,_0,_0,_0.06),_0_22.3px_17.9px_rgba(0,_0,_0,_0.072),_0_41.8px_33.4px_rgba(0,_0,_0,_0.086),_0_100px_80px_rgba(0,_0,_0,_0.12)]">
          <div className="absolute top-0 left-0 z-10 w-full h-full rounded-xl overflow-hidden ring-2 ring-white/20 ring-inset" />

          <Image
            src={selfieImageUrl}
            alt="Model selfie"
            width={40}
            height={40}
            className="w-full h-full object-cover"
          />
        </div>
      )}
      {showSelfie && (
        <AiGeneratedBadge
          variant="svg"
          className="absolute w-3/4 lg:w-1/2 lg:max-w-40 bottom-4 left-1/2 -translate-x-1/2 z-10"
        />
      )}

      {showAdminActions && user?.isAdmin && id && (
        <Button
          variant="overlay"
          size="icon"
          onClick={handleRerun}
          disabled={isRerunning}
          className="absolute top-2 right-2 hidden group-hover:flex z-20 h-8 w-8"
        >
          <RefreshCw className={`h-4 w-4 ${isRerunning ? 'animate-spin' : ''}`} />
        </Button>
      )}
      <div className="relative">
        <div className={cn('relative overflow-hidden', forceTallAspectRatio ? 'aspect-[2/3]' : 'aspect-auto')}>
          {imageUrl && (
            <img
              src={transformCloudflareImageUrl(imageUrl, { variant: cloudflareVariant })}
              alt=""
              className={cn(
                'transition-all duration-200 object-cover',
                isRerunning ? 'opacity-50 blur-md' : '',
                forceTallAspectRatio ? 'aspect-[2/3]' : 'aspect-auto'
              )}
            />
          )}
          {isRerunning && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="px-4 py-2 flex flex-col items-center gap-2 min-w-[200px]">
                <AnimatedProgressBar startTime={new Date()} />
                <span className="text-xs font-medium">Regenerating...</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
